import { useRef, useState, useEffect, useCallback } from 'react';
import haptic from '../../utils/haptics';
import { useTheme } from '../../contexts/ThemeContext';

export default function SlideToForfeit({ onConfirm, disabled = false }) {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const trackRef = useRef(null);
  const animRef = useRef(null);
  const startPointerXRef = useRef(0);
  const startThumbRef = useRef(0);
  const thumbXRef = useRef(0);
  const maxXRef = useRef(0);
  const [thumbX, setThumbX] = useState(0);
  const [maxX, setMaxX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const THUMB = 44;
  const COMPLETE_RATIO = 0.88;

  useEffect(() => { thumbXRef.current = thumbX; }, [thumbX]);
  useEffect(() => { maxXRef.current = maxX; }, [maxX]);

  useEffect(() => {
    const measure = () => {
      const el = trackRef.current;
      if (!el) return;
      setMaxX(Math.max(0, el.clientWidth - THUMB - 4));
    };
    measure();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && trackRef.current) {
      ro = new ResizeObserver(measure);
      ro.observe(trackRef.current);
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', measure);
    }
    return () => {
      if (ro) ro.disconnect();
      else if (typeof window !== 'undefined') window.removeEventListener('resize', measure);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  const animateTo = useCallback((target, onDone) => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    let last = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const step = (now) => {
      const dt = Math.min(0.05, ((now || Date.now()) - last) / 1000);
      last = now || Date.now();
      const current = thumbXRef.current;
      const diff = target - current;
      if (Math.abs(diff) < 0.5) {
        thumbXRef.current = target;
        setThumbX(target);
        animRef.current = null;
        if (onDone) onDone();
        return;
      }
      const next = current + diff * Math.min(1, dt * 14);
      thumbXRef.current = next;
      setThumbX(next);
      animRef.current = requestAnimationFrame(step);
    };
    animRef.current = requestAnimationFrame(step);
  }, []);

  const handlePointerDown = (e) => {
    if (disabled || confirming) return;
    e.preventDefault();
    e.stopPropagation();
    if (animRef.current) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }
    setDragging(true);
    startPointerXRef.current = e.clientX;
    startThumbRef.current = thumbXRef.current;
    try { e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };

  const handlePointerMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - startPointerXRef.current;
    const next = Math.max(0, Math.min(maxXRef.current, startThumbRef.current + dx));
    thumbXRef.current = next;
    setThumbX(next);
  };

  const handlePointerUp = (e) => {
    if (!dragging) return;
    setDragging(false);
    try { e.currentTarget.releasePointerCapture && e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    const max = maxXRef.current;
    if (max > 0 && thumbXRef.current / max >= COMPLETE_RATIO) {
      animateTo(max, () => {
        try { haptic.warning(); } catch {}
        setConfirming(true);
        Promise.resolve(onConfirm && onConfirm()).catch(() => {}).finally(() => {
          setConfirming(false);
          animateTo(0);
        });
      });
    } else {
      animateTo(0);
    }
  };

  const progress = maxX > 0 ? thumbX / maxX : 0;
  const baseLayer = isLight
    ? `linear-gradient(180deg, rgba(254,242,242,0.95), rgba(255,247,237,0.95))`
    : `linear-gradient(180deg, rgba(30,22,24,0.85), rgba(18,14,16,0.85))`;
  const trackBorder = isLight
    ? `1px solid rgba(220,38,38,${0.30 + progress * 0.40})`
    : `1px solid rgba(239,68,68,${0.30 + progress * 0.45})`;
  const trackShadow = isLight
    ? `inset 0 1px 0 rgba(255,255,255,0.6), 0 1px 3px rgba(220,38,38,0.12)`
    : `inset 0 1px 0 rgba(255,255,255,0.05), 0 1px 4px rgba(0,0,0,0.35)`;
  const labelColor = isLight ? '#dc2626' : '#fca5a5';
  // Solid fill the thumb drags along with it — unmistakable "you are dragging
  // this" feedback (the old version only nudged some alpha values, which read
  // as nothing happening).
  const fillWidth = thumbX + THUMB + 4;
  const fillVisible = confirming || dragging || thumbX > 1;
  const releaseHint = Math.max(0, Math.min(1, (progress - 0.5) / 0.3));

  return (
    <div
      ref={trackRef}
      style={{
        position: 'relative',
        height: 52,
        borderRadius: 26,
        background: baseLayer,
        border: trackBorder,
        boxShadow: trackShadow,
        overflow: 'hidden',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        touchAction: 'none',
        WebkitTouchCallout: 'none',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {/* Solid red fill dragged along by the thumb */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: confirming ? '100%' : fillWidth,
          borderRadius: 26,
          background: 'linear-gradient(90deg, #b91c1c 0%, #dc2626 60%, #ef4444 100%)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(0,0,0,0.25)',
          opacity: fillVisible ? 1 : 0,
          pointerEvents: 'none',
          transition: dragging
            ? 'opacity 120ms ease'
            : 'width 220ms cubic-bezier(0.22,1,0.36,1), opacity 200ms ease',
        }}
      />
      {/* Resting label — slides away ahead of the thumb instead of being run over */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          paddingLeft: THUMB + 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          color: labelColor,
          fontSize: 12.5,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          opacity: confirming ? 0 : Math.max(0, 1 - progress * 2.2),
          transform: `translateX(${progress * 28}px)`,
          transition: dragging ? 'none' : 'opacity 200ms ease, transform 220ms cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        Slide to forfeit
      </div>
      {/* Inside-the-fill label — appears as the fill takes over */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          color: '#fff',
          fontSize: 12.5,
          fontWeight: 800,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          textShadow: '0 1px 2px rgba(0,0,0,0.35)',
          opacity: confirming ? 1 : releaseHint,
          transition: dragging ? 'none' : 'opacity 200ms ease',
        }}
      >
        {confirming ? 'Forfeiting…' : 'Release to forfeit'}
      </div>
      <div
        role="button"
        aria-label="Slide to forfeit battle"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onClick={(e) => { e.stopPropagation(); }}
        style={{
          position: 'absolute',
          top: '50%',
          left: 4,
          width: THUMB,
          height: THUMB,
          borderRadius: '50%',
          background: 'linear-gradient(180deg, #f87171 0%, #dc2626 100%)',
          border: dragging ? '2px solid rgba(255,255,255,0.85)' : '1px solid rgba(255,255,255,0.35)',
          boxShadow: dragging
            ? '0 4px 14px rgba(220,38,38,0.6), 0 0 0 6px rgba(239,68,68,0.18), inset 0 1px 0 rgba(255,255,255,0.35)'
            : '0 2px 6px rgba(220,38,38,0.45), inset 0 1px 0 rgba(255,255,255,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          transform: `translate(${thumbX}px, -50%) scale(${dragging ? 1.08 : 1})`,
          transition: dragging
            ? 'border 120ms ease, box-shadow 120ms ease'
            : 'transform 220ms cubic-bezier(0.22,1,0.36,1), border 120ms ease, box-shadow 120ms ease',
          cursor: disabled || confirming ? 'not-allowed' : dragging ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
}
